import { request } from '@/services/apiClient';
import type {
	AuthUser,
	LoginCredentials,
	LoginResult,
	RefreshTokens,
} from '../types';

/** All auth-related network calls live here. Components never call axios. */
export const authApi = {
	/**
	 * Authenticate with credentials. Returns either a session or an MFA
	 * challenge the client must complete via {@link mfaApi.verifyMfa}. The
	 * backend finds the account's tenant from the email.
	 */
	login(credentials: LoginCredentials): Promise<LoginResult> {
		return request<LoginResult>({
			url: '/v1/auth/login',
			method: 'POST',
			data: credentials,
			// A 401 here means bad credentials, not an expired token.
			skipAuthRefresh: true,
		});
	},

	/** Exchange a refresh token for a new access token. */
	refresh(refreshToken: string): Promise<RefreshTokens> {
		return request<RefreshTokens>({
			url: '/v1/auth/refresh',
			method: 'POST',
			data: { refreshToken },
			// Never triggers the interceptor's refresh flow (avoids a loop).
			skipAuthRefresh: true,
		});
	},

	fetchProfile(): Promise<AuthUser> {
		return request<AuthUser>({ url: '/v1/auth/me', method: 'GET' });
	},

	logout(refreshToken: string): Promise<void> {
		return request<void>({
			url: '/v1/auth/logout',
			method: 'POST',
			data: { refreshToken },
			// The session is being torn down; never try to refresh it.
			skipAuthRefresh: true,
		});
	},
};
